import { useState, useCallback } from "react";
import { useAccount, useWriteContract, useSwitchChain } from "wagmi";
import { mainnet } from "wagmi/chains";
// Import Paxos SDK functions
import {
  prepareDepositAndBridgeTransactionData,
  prepareWithdrawTransactionData,
} from "@molecularlabs/nucleus-frontend";
import { INITIAL_PAXOS_STATE, type PaxosBridgeState } from "@/lib/paxos/config";
import { PAXOS_SDK_CONFIG } from "@/constants";

export type PaxosBridgeDirection = "eth-to-camp" | "camp-to-eth";

interface PaxosBridgeParams {
  to: string;
  amount: string;
  token: {
    id: string;
    symbol: string;
    decimals: number;
  };
}

export function usePaxosBridge() {
  const { address, chain } = useAccount();
  const { writeContractAsync } = useWriteContract();
  const { switchChain } = useSwitchChain();
  const [bridgeState, setBridgeState] =
    useState<PaxosBridgeState>(INITIAL_PAXOS_STATE);

  // Deposit and Bridge: Ethereum → Camp Network (USDC → CampUSD)
  const depositAndBridge = useCallback(
    async (params: PaxosBridgeParams) => {
      if (!address || !params.to) {
        throw new Error("Wallet not connected or invalid recipient address");
      }

      console.log("🚀 Starting Paxos depositAndBridge:", {
        from: "Ethereum",
        to: "Camp Network",
        amount: params.amount,
        token: params.token.symbol,
        userAddress: address,
        recipient: params.to,
      });

      setBridgeState({
        isLoading: true,
        isSuccess: false,
        isError: false,
        error: null,
        isPreparing: true,
        paxosStep: "preparing",
      });

      try {
        // Prepare deposit and bridge transaction using Paxos SDK
        console.log("📝 Preparing depositAndBridge transaction...");

        const depositData = await prepareDepositAndBridgeTransactionData({
          vaultKey: PAXOS_SDK_CONFIG.VAULT_KEYS.CAMPUSD, // "campUSD"
          depositTokenSymbol: PAXOS_SDK_CONFIG.TOKENS.SOURCE, // "USDC"
          depositAmount: params.amount,
          sourceChainId: PAXOS_SDK_CONFIG.SUPPORTED_CHAINS.ETHEREUM, // Ethereum mainnet
          destinationChainId: PAXOS_SDK_CONFIG.SUPPORTED_CHAINS.CAMP, // Camp Network
          userAddress: params.to as `0x${string}`, // Recipient address
        });

        console.log("✅ Paxos deposit transaction prepared:", depositData);

        // Update state to indicate preparation complete
        setBridgeState((prev) => ({
          ...prev,
          isPreparing: false,
          isExecuting: true,
          paxosStep: "executing",
        }));

        // Ensure we're on the correct chain (Ethereum for deposits)
        if (chain?.id !== PAXOS_SDK_CONFIG.SUPPORTED_CHAINS.ETHEREUM) {
          console.log("🔄 Switching to Ethereum for deposit transaction...");
          await switchChain({
            chainId: PAXOS_SDK_CONFIG.SUPPORTED_CHAINS.ETHEREUM,
          });
        }

        // Execute the transaction using wagmi
        console.log("🚀 Executing Paxos deposit transaction...");
        const txHash = await writeContractAsync({
          address: depositData.address,
          abi: depositData.abi,
          functionName: depositData.functionName,
          args: depositData.args,
          value: depositData.value, // Include bridge fee if required
          chainId: depositData.chainId,
        });

        setBridgeState({
          isLoading: false,
          isSuccess: true,
          isError: false,
          error: null,
          txHash: txHash,
          isPreparing: false,
          isExecuting: false,
          paxosStep: "confirming",
        });

        console.log("✅ Paxos depositAndBridge completed successfully", {
          txHash,
        });
        return txHash;
      } catch (error) {
        console.error("❌ Paxos depositAndBridge failed:", error);

        const errorMessage =
          error instanceof Error
            ? error
            : new Error("Paxos deposit and bridge failed");
        setBridgeState({
          isLoading: false,
          isSuccess: false,
          isError: true,
          error: errorMessage,
          isPreparing: false,
          isExecuting: false,
        });

        throw errorMessage;
      }
    },
    [address, chain, switchChain, writeContractAsync],
  );

  // Bridge and Withdraw: Camp Network → Ethereum (CampUSD → USDC)
  const bridgeAndWithdraw = useCallback(
    async (params: PaxosBridgeParams) => {
      if (!address || !params.to) {
        throw new Error("Wallet not connected or invalid recipient address");
      }

      console.log("🚀 Starting Paxos bridgeAndWithdraw:", {
        from: "Camp Network",
        to: "Ethereum",
        amount: params.amount,
        token: params.token.symbol,
        userAddress: address,
        recipient: params.to,
      });

      setBridgeState({
        isLoading: true,
        isSuccess: false,
        isError: false,
        error: null,
        isPreparing: true,
        paxosStep: "preparing",
      });

      try {
        console.log("📝 Preparing bridgeAndWithdraw transaction...");

        // For withdraw (Camp → Ethereum), we use prepareWithdrawTransactionData
        const withdrawData = await prepareWithdrawTransactionData({
          vaultKey: PAXOS_SDK_CONFIG.VAULT_KEYS.CAMPUSD, // "campUSD"
          chainId: PAXOS_SDK_CONFIG.SUPPORTED_CHAINS.CAMP, // Camp Network
          userAddress: address as `0x${string}`,
          wantTokenSymbol: PAXOS_SDK_CONFIG.TOKENS.SOURCE, // Want to receive USDC
          offerAmount: params.amount, // Amount of vault shares to withdraw
        });

        console.log("✅ Paxos withdraw transaction prepared:", withdrawData);

        setBridgeState((prev) => ({
          ...prev,
          isPreparing: false,
          isExecuting: true,
          paxosStep: "executing",
        }));

        // Ensure we're on the correct chain (Camp Network for withdrawals)
        if (chain?.id !== PAXOS_SDK_CONFIG.SUPPORTED_CHAINS.CAMP) {
          console.log(
            "🔄 Switching to Camp Network for withdraw transaction...",
          );
          await switchChain({
            chainId: PAXOS_SDK_CONFIG.SUPPORTED_CHAINS.CAMP,
          });
        }

        // Execute the transaction using wagmi
        console.log("🚀 Executing Paxos withdraw transaction...");
        const txHash = await writeContractAsync({
          address: withdrawData.address,
          abi: withdrawData.abi,
          functionName: withdrawData.functionName,
          args: withdrawData.args,
          chainId: withdrawData.chainId,
        });

        setBridgeState({
          isLoading: false,
          isSuccess: true,
          isError: false,
          error: null,
          txHash: txHash,
          isPreparing: false,
          isExecuting: false,
          paxosStep: "confirming",
        });

        console.log("✅ Paxos bridgeAndWithdraw completed successfully", {
          txHash,
        });
        return txHash;
      } catch (error) {
        console.error("❌ Paxos bridgeAndWithdraw failed:", error);

        const errorMessage =
          error instanceof Error
            ? error
            : new Error("Paxos bridge and withdraw failed");
        setBridgeState({
          isLoading: false,
          isSuccess: false,
          isError: true,
          error: errorMessage,
          isPreparing: false,
          isExecuting: false,
        });

        throw errorMessage;
      }
    },
    [address, chain, switchChain, writeContractAsync],
  );

  // Main bridge function that routes based on direction
  const bridge = useCallback(
    async (direction: PaxosBridgeDirection, params: PaxosBridgeParams) => {
      console.log("🌉 Paxos bridge called:", { direction, params });

      if (direction === "eth-to-camp") {
        return depositAndBridge(params);
      } else if (direction === "camp-to-eth") {
        return bridgeAndWithdraw(params);
      } else {
        throw new Error(`Unsupported Paxos bridge direction: ${direction}`);
      }
    },
    [depositAndBridge, bridgeAndWithdraw],
  );

  // Check if transaction can be executed
  const canBridge = useCallback(
    (_direction: PaxosBridgeDirection, amount: string): boolean => {
      if (!address || !amount) return false;

      const bridgeAmount = parseFloat(amount);
      return bridgeAmount > 0;
    },
    [address],
  );

  // Get required chain for the bridge operation
  const getRequiredChain = useCallback((direction: PaxosBridgeDirection) => {
    return direction === "eth-to-camp"
      ? mainnet
      : { id: PAXOS_SDK_CONFIG.SUPPORTED_CHAINS.CAMP, name: "Camp Network" };
  }, []);

  // Check if user is on the correct chain
  const isOnCorrectChain = useCallback(
    (direction: PaxosBridgeDirection) => {
      const requiredChain = getRequiredChain(direction);
      return chain?.id === requiredChain.id;
    },
    [chain, getRequiredChain],
  );

  return {
    // State
    bridgeState,

    // Actions
    bridge,
    depositAndBridge,
    bridgeAndWithdraw,
    canBridge,

    // Utilities
    getRequiredChain,
    isOnCorrectChain,

    // Configuration
    config: PAXOS_SDK_CONFIG,
  };
}
